import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import {
  type PlatformOverview,
  useAdjustCredits,
  useAiAdmin,
  useClubDetail,
  useGrantPlan,
  usePlatformOverview,
  useSetAiSetting,
} from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { formatDate } from '@/lib/format';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextInput } from '@/ui/Field';
import { Chip } from '@/ui/Chip';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Sheet, SheetRow } from '@/ui/Sheet';

const WINDOWS = [7, 30, 90, 365];

/**
 * Isolate a Latin run so it survives being read inside Arabic.
 *
 * "$15.00" and "1 / 4" are digits around a neutral character, which the bidi
 * algorithm resolves against the paragraph it sits in: in RTL they come out as
 * "15.00$" and "4 / 1" — not a cosmetic flip but a different number. The
 * isolates are invisible and do nothing in English and French, so every money
 * and ratio string gets them at the point it is built rather than at each of
 * the places it is shown.
 */
function ltr(text: string): string {
  return `\u2066${text}\u2069`;
}

function money(cents: number, currency: string | null): string {
  return ltr(`${(cents / 100).toFixed(2)} ${(currency ?? 'usd').toUpperCase()}`);
}

function usd(amount: number): string {
  return ltr(`$${amount.toFixed(2)}`);
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <div className="text-meta text-ink-muted">{label}</div>
      <div className="tabular font-condensed text-headline font-bold">{value}</div>
      {hint ? <div className="text-meta text-ink-faint">{hint}</div> : null}
    </div>
  );
}

/**
 * Running MyPlannerPad, as opposed to running a club.
 *
 * Every club, what they pay, what they have left and what they cost us, with the
 * two levers that cannot be automated: a plan granted by hand and a credit
 * adjustment. Both demand a reason in writing, because a grant with no reason is
 * indistinguishable from a mistake six months later — and the reason is stored
 * with the actor, not just the number.
 *
 * The flag that opens this screen is set from outside the app
 * (`scripts/platform-admin.py`). There is no endpoint that promotes a user: an
 * admin screen that can promote its own users is one compromised session away
 * from being everybody's billing.
 */
export function Admin({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { user } = useAuthSession();
  const [days, setDays] = useState(30);
  const [openClub, setOpenClub] = useState<string | null>(null);
  const overview = usePlatformOverview(days, Boolean(user?.isPlatformAdmin));

  if (!user?.isPlatformAdmin) {
    return (
      <Panel title={t('admin.title')}>
        <EmptyState title={t('admin.notAdmin')} body={t('admin.notAdminBody')} />
      </Panel>
    );
  }

  if (overview.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }
  if (overview.isError || !overview.data) return <Notice tone="risk">{t('error.generic')}</Notice>;

  const { totals, clubs } = overview.data;
  // Margin only means something when one currency came in: the AI cost is in
  // dollars, and subtracting it from a euro total is a number, not an answer.
  const oneCurrency =
    totals.settledBy.length === 0 ||
    (totals.settledBy.length === 1 && totals.settledBy[0]!.currency === 'usd');
  const margin = totals.settledCents / 100 - totals.aiCostUsdRecent;

  return (
    <>
      <PageHeader title={t('admin.title')} lead={t('admin.lead')} />

      <div className="grid gap-5">
        <Panel
          title={t('admin.overview')}
          action={
            <div className="w-36">
              <SelectInput
                aria-label={t('admin.window')}
                value={String(days)}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                {WINDOWS.map((d) => (
                  <option key={d} value={d}>
                    {t('admin.lastDays', { count: d })}
                  </option>
                ))}
              </SelectInput>
            </div>
          }
        >
          <div className="grid gap-5 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label={t('admin.clubs')}
              value={String(totals.clubs)}
              hint={t('admin.newInWindow', { count: totals.clubsRecent })}
            />
            <Stat
              label={t('admin.paying')}
              value={ltr(`${totals.paidClubs} / ${totals.clubs}`)}
              hint={t('admin.freeClubs', { count: totals.freeClubs })}
            />
            <Stat
              label={t('admin.signups')}
              value={String(totals.signupsRecent)}
              hint={t('admin.usersTotal', { count: totals.users })}
            />
            <Stat
              label={t('admin.settled')}
              value={money(totals.settledCents, totals.settledCurrency)}
              hint={
                totals.settledBy.length > 1
                  ? totals.settledBy
                      .slice(1)
                      .map((r) => money(r.cents, r.currency))
                      .join(' · ')
                  : t('admin.payments', { count: totals.payments })
              }
            />
            <Stat
              label={t('admin.creditsOutstanding')}
              value={String(totals.creditsOutstanding)}
              hint={t('admin.creditsOutstandingHint')}
            />
            <Stat
              label={t('admin.aiSpend')}
              value={usd(totals.aiCostUsdRecent)}
              hint={t('admin.aiSpendTotal', { total: usd(totals.aiCostUsdTotal) })}
            />
            <Stat
              label={t('admin.margin')}
              value={oneCurrency ? usd(margin) : '—'}
              hint={oneCurrency ? t('admin.marginHint') : t('admin.marginMixed')}
            />
          </div>
        </Panel>

        <AiBill days={days} />

        <Panel title={t('admin.allClubs')} count={clubs.length || undefined}>
          {clubs.length === 0 ? (
            <EmptyState title={t('admin.noClubs')} body={t('admin.noClubsBody')} />
          ) : (
            <Sheet>
              {clubs.map((c) => (
                <li key={c.orgId}>
                  <SheetRow onClick={() => setOpenClub(openClub === c.orgId ? null : c.orgId)}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{c.name}</span>
                      <span className="text-meta text-ink-faint">
                        {c.users.length
                          ? ltr(c.users.map((u) => u.email).join(' · '))
                          : t('admin.noUsers')}
                      </span>
                    </span>
                    <span className="tabular text-meta text-ink-muted">
                      {t('admin.creditsShort', { count: c.credits })}
                    </span>
                    {c.aiCostUsd > 0 ? (
                      <span className="tabular text-meta text-ink-muted">{usd(c.aiCostUsd)}</span>
                    ) : null}
                    {c.grantedPlan ? <Chip muted>{t('admin.granted')}</Chip> : null}
                    <Chip>{t(`admin.plan_${c.plan}`)}</Chip>
                  </SheetRow>
                  {openClub === c.orgId ? (
                    <ClubPanel club={c} locale={locale} />
                  ) : null}
                </li>
              ))}
            </Sheet>
          )}
        </Panel>
      </div>
    </>
  );
}

function ClubPanel({
  club,
  locale,
}: {
  club: PlatformOverview['clubs'][number];
  locale: Locale;
}) {
  const t = useTranslations();
  const detail = useClubDetail(club.orgId);
  const grant = useGrantPlan(club.orgId);
  const adjust = useAdjustCredits(club.orgId);

  const [plan, setPlan] = useState<'free' | 'pro' | 'none'>(
    (club.grantedPlan as 'free' | 'pro') ?? 'none',
  );
  const [until, setUntil] = useState(
    club.grantedUntil ? new Date(club.grantedUntil).toISOString().slice(0, 10) : '',
  );
  const [planReason, setPlanReason] = useState('');
  const [delta, setDelta] = useState('');
  const [creditReason, setCreditReason] = useState('');

  return (
    <div className="border-t border-line bg-surface-sunken">
      <Section title={t('admin.state')}>
        <dl className="grid gap-x-5 gap-y-2 text-meta sm:grid-cols-2 lg:grid-cols-4">
          {[
            [t('admin.storedPlan'), ltr(`${club.storedPlan}${club.interval ? ` · ${club.interval}` : ''}`)],
            [t('admin.status'), club.status ?? '—'],
            [t('admin.renews'), club.currentPeriodEnd ? formatDate(new Date(club.currentPeriodEnd).toISOString().slice(0, 10), locale) : '—'],
            [t('admin.creditsBalance'), String(club.credits)],
            [t('admin.creditsGranted'), String(club.creditsGranted)],
            [t('admin.creditsSpent'), String(club.creditsSpent)],
            [t('admin.aiCalls'), String(club.aiCalls)],
            [t('admin.aiCost'), usd(club.aiCostUsd)],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-ink-muted">{label}</dt>
              <dd className="tabular font-medium">{value}</dd>
            </div>
          ))}
        </dl>
        {club.grantedPlan ? (
          <div className="mt-3">
            <Notice tone="info">
              {t('admin.grantedNote', {
                plan: ltr(club.grantedPlan),
                until: club.grantedUntil
                  ? formatDate(new Date(club.grantedUntil).toISOString().slice(0, 10), locale)
                  : t('admin.noEnd'),
              })}
            </Notice>
          </div>
        ) : null}
      </Section>

      <Section title={t('admin.accounts')}>
        <ul className="grid gap-2 text-meta">
          {club.users.map((u) => (
            <li key={u.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-medium">{u.name}</span>
              <span className="min-w-0 truncate text-ink-muted">{ltr(u.email)}</span>
              <span className="ms-auto text-ink-faint">
                {t('admin.signedUp', {
                  on: formatDate(new Date(u.createdAt).toISOString().slice(0, 10), locale),
                })}
              </span>
              <span className="w-40 text-end text-ink-faint">
                {u.lastLoginAt
                  ? t('admin.lastSeen', {
                      on: formatDate(
                        new Date(u.lastLoginAt).toISOString().slice(0, 10),
                        locale,
                      ),
                    })
                  : t('admin.neverSignedIn')}
              </span>
            </li>
          ))}
          {club.users.length === 0 ? (
            <li className="text-ink-muted">{t('admin.noUsers')}</li>
          ) : null}
        </ul>
      </Section>

      <Section title={t('admin.grantPlan')} hint={t('admin.grantPlanHint')}>
        <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-[13rem_11rem_1fr_auto]">
          <Field label={t('admin.plan')}>
            {(p) => (
              <SelectInput {...p} value={plan} onChange={(e) => setPlan(e.target.value as typeof plan)}>
                <option value="none">{t('admin.noGrant')}</option>
                <option value="pro">{t('admin.plan_pro')}</option>
                <option value="free">{t('admin.plan_free')}</option>
              </SelectInput>
            )}
          </Field>
          <Field label={t('admin.until')} optional>
            {(p) => <TextInput {...p} type="date" value={until} onChange={(e) => setUntil(e.target.value)} />}
          </Field>
          <Field label={t('admin.reason')}>
            {(p) => (
              <TextInput
                {...p}
                value={planReason}
                placeholder={t('admin.reasonPlaceholder')}
                onChange={(e) => setPlanReason(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-end">
            <Button
              variant="primary"
              busy={grant.isPending}
              disabled={!planReason.trim()}
              onClick={async () => {
                await grant.mutateAsync({
                  plan: plan === 'none' ? null : plan,
                  until: until || null,
                  reason: planReason.trim(),
                });
                setPlanReason('');
              }}
            >
              {t('admin.apply')}
            </Button>
          </div>
        </div>
        {grant.isError ? (
          <div className="mt-3">
            <Notice tone="risk">{t('common.saveFailed')}</Notice>
          </div>
        ) : null}
      </Section>

      <Section title={t('admin.adjustCredits')} hint={t('admin.adjustCreditsHint')}>
        <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-[9rem_1fr_auto]">
          <Field label={t('admin.delta')}>
            {(p) => (
              <TextInput
                {...p}
                type="number"
                value={delta}
                placeholder="50"
                onChange={(e) => setDelta(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.reason')}>
            {(p) => (
              <TextInput
                {...p}
                value={creditReason}
                placeholder={t('admin.creditReasonPlaceholder')}
                onChange={(e) => setCreditReason(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-end">
            <Button
              variant="primary"
              busy={adjust.isPending}
              disabled={!creditReason.trim() || !Number(delta)}
              onClick={async () => {
                await adjust.mutateAsync({ delta: Number(delta), reason: creditReason.trim() });
                setDelta('');
                setCreditReason('');
              }}
            >
              {t('admin.apply')}
            </Button>
          </div>
        </div>
        {adjust.isError ? (
          <div className="mt-3">
            <Notice tone="risk">{t('common.saveFailed')}</Notice>
          </div>
        ) : null}
      </Section>

      <Section title={t('admin.movements')}>
        {detail.isLoading ? (
          <p className="flex items-center gap-2 text-meta text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : (
          <div className="grid gap-4 text-meta">
            <ul className="grid gap-1">
              {(detail.data?.ledger ?? []).slice(0, 12).map((l) => (
                <li key={l.id} className="flex gap-3">
                  <span className="tabular w-16 text-end font-semibold">
                    {ltr(l.delta > 0 ? `+${l.delta}` : String(l.delta))}
                  </span>
                  <span className="text-ink-muted">{t(`admin.reason_${l.reason}`)}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-faint">{l.note ?? ''}</span>
                  <span className="tabular text-ink-faint">
                    {formatDate(new Date(l.createdAt).toISOString().slice(0, 10), locale)}
                  </span>
                </li>
              ))}
              {(detail.data?.ledger ?? []).length === 0 ? (
                <li className="text-ink-muted">{t('admin.noMovements')}</li>
              ) : null}
            </ul>
            {(detail.data?.usage ?? []).length > 0 ? (
              <ul className="grid gap-1 border-t border-line pt-3">
                {(detail.data?.usage ?? []).slice(0, 8).map((u) => (
                  <li key={u.id} className="flex gap-3">
                    {/* The feature key as stored — only the owner reads this, and a
                        translated name would hide which code path spent the credit. */}
                    <span className="min-w-0 flex-1 truncate">{ltr(u.feature)}</span>
                    <span className="text-ink-faint">{ltr(u.model)}</span>
                    <span className="tabular">{ltr(`${u.credits} cr`)}</span>
                    <span className="tabular text-ink-faint">{usd(u.costUsd)}</span>
                    <span className="text-ink-faint">{u.outcome}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {(detail.data?.events ?? []).length > 0 ? (
              <ul className="grid gap-1 border-t border-line pt-3">
                {(detail.data?.events ?? []).slice(0, 8).map((e) => (
                  <li key={e.id} className="flex gap-3">
                    <span className="min-w-0 flex-1 truncate">{e.type}</span>
                    {e.amountCents ? (
                      <span className="tabular">{money(e.amountCents, e.currency)}</span>
                    ) : null}
                    <span className="text-ink-faint">{e.outcome}</span>
                    <span className="tabular text-ink-faint">
                      {formatDate(new Date(e.receivedAt).toISOString().slice(0, 10), locale)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </Section>
    </div>
  );
}

/**
 * What the assistants cost, and the two switches that stop them.
 *
 * Spend is shown against the ceiling rather than on its own: a figure with no
 * ceiling beside it tells you nothing about whether to worry, and worrying is
 * the only reason to open this panel. The kill switch sits here rather than in
 * a config file because the moment it is needed is the moment a deploy is too
 * slow.
 */
function AiBill({ days }: { days: number }) {
  const t = useTranslations();
  const ai = useAiAdmin(days);
  const set = useSetAiSetting();
  if (!ai.data) return null;
  const { settings } = ai.data;

  const todayShare = settings.platformDailyCapUsd
    ? Math.min(100, Math.round((ai.data.spentTodayUsd / settings.platformDailyCapUsd) * 100))
    : 0;

  return (
    <Panel
      title={t('admin.ai')}
      action={
        <Button
          variant={settings.enabled ? 'secondary' : 'primary'}
          busy={set.isPending}
          className="!min-h-9 !text-meta"
          onClick={() => set.mutate({ key: 'ai.enabled', value: !settings.enabled })}
        >
          {settings.enabled ? t('admin.aiOn') : t('admin.aiOff')}
        </Button>
      }
    >
      <Section title={t('admin.aiSwitch')} hint={t('admin.aiHint')}>
        {!settings.enabled ? (
          <div className="mb-4">
            <Notice tone="risk">{t('admin.aiOffNote')}</Notice>
          </div>
        ) : null}

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label={t('admin.aiSpentToday')}
            value={usd(ai.data.spentTodayUsd)}
            hint={t('admin.aiCallsToday', { count: ai.data.callsToday })}
          />
          <Stat
            label={t('admin.aiPlatformCap')}
            value={usd(settings.platformDailyCapUsd)}
            hint={ltr(`${todayShare}%`)}
          />
          <Stat
            label={t('admin.aiSpentWindow')}
            value={usd(ai.data.spentUsd)}
            hint={t('admin.aiCalls', { count: ai.data.calls })}
          />
          <Stat
            label={t('admin.aiOrgCap')}
            value={usd(settings.orgDailyCapUsd)}
            hint={ai.data.failed > 0 ? t('admin.aiFailed', { count: ai.data.failed }) : undefined}
          />
        </div>

        {/* A bar, because a percentage of a ceiling is the one number worth a glance. */}
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-line">
          <div
            className={`h-full ${todayShare > 80 ? 'bg-risk' : 'bg-signal'}`}
            style={{ width: `${todayShare}%` }}
          />
        </div>
      </Section>

      <Section title={t('admin.aiModel')} hint={t('admin.aiCapHint')}>
        <ul className="grid gap-2 text-meta">
          {ai.data.byFeature.length === 0 ? (
            <li className="text-ink-muted">{t('admin.aiNoCalls')}</li>
          ) : (
            ai.data.byFeature.map((f) => (
              <li key={f.feature} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="min-w-0 flex-1 font-medium">
                  {t(`admin.feature_${f.feature}`)}
                </span>
                <span className="text-ink-faint">{ltr(settings.models[f.feature] ?? '—')}</span>
                <span className="tabular">{t('admin.aiCalls', { count: f.calls })}</span>
                <span className="tabular w-20 text-end">{usd(f.costUsd)}</span>
                <span className="tabular w-20 text-end text-ink-faint">
                  {ltr(`${f.credits} cr`)}
                </span>
              </li>
            ))
          )}
        </ul>
      </Section>
    </Panel>
  );
}
